import { FC, useEffect, useState } from 'react';
import {
  Box,
  DetailsAccordion,
  FormInputText,
  Skeleton,
  Svg,
  SvgBubble,
  Text,
  Tooltip,
  ViewEditField,
} from '@hopskipdrive/react-components';

import { getCalendarDetails } from '../../../api/services/Calendars';
import { ApiResponse } from '../../../api/utils/responseTransform';
import { DROPOFF, PICKUP } from '../../../constants';
import { actions, Can, subjects } from '../../../contexts/AbilityContext';
import { GroupedOption } from '../../../hooks/useLocationOptions';
import { LocationFormData } from '../../../locations/types';

import { EllipsisLink, LightText } from './LocationAccordion.styles';

interface LocationAccordionProps {
  waypointType: string;
  isFetching?: boolean;
  editMode: boolean;
  location: RideLocation & { instructions?: { dropoffProcedure: string; pickupProcedure: string } };
  locations: GroupedOption[];
  updateLocation?: (data: Partial<LocationFormData>) => Promise<ApiResponse<{ accountLocation: AccountLocation }>>;
  onRemove?: () => void;
  isReadOnly?: boolean;
  hideInstructions?: boolean;
  noEdit?: boolean;
}

const helpers = {
  [PICKUP]: {
    getLocationInstructions: () => 'Pickup instructions for all riders',
  },
  [DROPOFF]: {
    getLocationInstructions: () => 'Dropoff instructions for all riders',
  },
};

const DROPOFF_PROCEDURE = 'dropoffProcedure';
const PICKUP_PROCEDURE = 'pickupProcedure';

export const LocationAccordion: FC<LocationAccordionProps> = ({
  isFetching,
  editMode,
  waypointType,
  location,
  locations,
  updateLocation,
  onRemove,
  isReadOnly = false,
  hideInstructions = false,
  noEdit = false,
}) => {
  // The only data that can change for a location is the instructions
  const isDropoffForLocation = waypointType === DROPOFF;
  const type = isDropoffForLocation ? DROPOFF_PROCEDURE : PICKUP_PROCEDURE;
  const locationDetails = locations.find((loc) => loc.placeId == location.placeId);
  const defaultInstructionsValue =
    (location.instructions && location.instructions[type]) ||
    (locationDetails && locationDetails[`${type}Instructions`]) ||
    '';
  const [instructionsValue, setInstructionsValue] = useState(defaultInstructionsValue);
  const [instructionsInputValue, setInstructionsInputValue] = useState(defaultInstructionsValue);
  const [instructionsError, setInstructionsError] = useState<string | null>(null);

  const saveInstructions = async () => {
    const instructionsChanged = instructionsInputValue !== (instructionsValue ?? '');

    if ((instructionsInputValue?.length ?? 0) > 500 && instructionsChanged) {
      setInstructionsError('Max. 500 characters');
      setInstructionsError(null);

      return false;
    }
    if (updateLocation) {
      const response = await updateLocation({
        id: parseInt(location.id),
        address: location.address,
        [`${type}Instructions`]: instructionsInputValue,
      });

      if (response.type === 'success') {
        setInstructionsValue(response.data.accountLocation[type].instructions);
      } else {
        setInstructionsValue(instructionsValue);
        setInstructionsInputValue(instructionsValue);
      }
    }

    return true;
  };

  const locationTitle = isReadOnly ? (
    <LightText>{location.name || location.address}</LightText>
  ) : (
    <EllipsisLink href={`/locations/${location.id}`} target="_blank">
      {location.name || location.address}
    </EllipsisLink>
  );

  return (
    <DetailsAccordion
      onRemove={onRemove ? onRemove : () => {}}
      textWhenExpanded="Hide"
      textWhenCollapsed="Show"
      iconComponent={<Svg icon="location-icon-small" color="#535353" />}
      disableRemove={!editMode}
      titleComponent={isFetching ? <Skeleton variant="text" width={200} /> : locationTitle}
    >
      {isFetching ? (
        <DetailsSkeleton>{hideInstructions ? '' : helpers[waypointType].getLocationInstructions()}</DetailsSkeleton>
      ) : (
        <>
          <Text variant="body1" fontWeight={300}>
            {locationDetails?.address}
          </Text>
          <CalendarName calendarSlug={locationDetails?.calendar} calendarName={locationDetails?.calendarName} />
          {!noEdit && (
            <>
              {' '}
              <Can I={actions.edit} a={subjects.location}>
                <ViewEditField
                  title={
                    hideInstructions ? (
                      ''
                    ) : (
                      <span style={{ display: 'flex', gap: 8 }}>
                        {helpers[waypointType].getLocationInstructions()}
                        <Tooltip
                          title="Location pickup and dropoff notes should highlight essential location details about the location specific pickup and dropoff experience to optimize the pickup/dropoff experience. Notes should be concise, complete, and actionable."
                          placement="right"
                        >
                          <span>
                            <SvgBubble size="big">
                              <Svg icon="question" />
                            </SvgBubble>
                          </span>
                        </Tooltip>
                      </span>
                    )
                  }
                  value={
                    hideInstructions ? (
                      ''
                    ) : (
                      <Text fontWeight={300} textColor="secondaryText">
                        {instructionsValue || 'This location has no special instructions'}
                      </Text>
                    )
                  }
                  onSave={saveInstructions}
                  onCancel={() => setInstructionsInputValue(instructionsValue)}
                  TextFieldComponent={
                    <FormInputText
                      variant="outlined"
                      multiline
                      rows={3}
                      fullWidth
                      value={instructionsInputValue}
                      onChange={(e) => setInstructionsInputValue(e.target.value)}
                      error={
                        (instructionsInputValue?.length ?? 0) > 500 &&
                        instructionsInputValue !== (instructionsValue ?? '')
                      }
                      helperText={(() => {
                        if (instructionsError) {
                          return instructionsError;
                        }
                        if (!instructionsInputValue || instructionsInputValue.length === 0) {
                          return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                        }

                        return (
                          <span>
                            {instructionsInputValue.length >= 500
                              ? `Max. ${Math.min(instructionsInputValue.length, 500)} characters`
                              : `${Math.min(instructionsInputValue.length, 500)}/500 characters`}
                          </span>
                        );
                      })()}
                      placeholder={
                        waypointType === 'pick_up'
                          ? 'Ex: School parking lot, Zone A, spot #3. Look for volunteer with orange vest'
                          : 'Ex: Bus lane drop-off, look for volunteer with orange vest'
                      }
                    />
                  }
                  canEdit={!isReadOnly}
                />
              </Can>
              <Can not I={actions.edit} a={subjects.location}>
                {!hideInstructions && (
                  <>
                    <Text>{helpers[waypointType].getLocationInstructions()}</Text>

                    <Text fontWeight={300} textColor="secondaryText">
                      {instructionsValue || 'This location has no special instructions'}
                    </Text>
                  </>
                )}
              </Can>
            </>
          )}
        </>
      )}
    </DetailsAccordion>
  );
};

export const LocationAccordionSkeleton = () => {
  return (
    <DetailsAccordion
      textWhenExpanded=""
      textWhenCollapsed=""
      iconComponent={<Svg icon="location-icon-small" color="#535353" />}
      titleComponent={<Skeleton variant="text" width={200} />}
      onRemove={() => null}
      disableRemove={true}
    >
      <DetailsSkeleton>{null}</DetailsSkeleton>
    </DetailsAccordion>
  );
};

const DetailsSkeleton = ({ children }) => {
  return (
    <>
      <Skeleton variant="text" width={180} />
      <Skeleton variant="text" width={140} />
      <Box display="flex" gap="8px">
        <Text textColor="secondaryText">{children}</Text>
        <Skeleton variant="text" width={20} />
      </Box>
      <Skeleton variant="text" width={200} />
    </>
  );
};

const CalendarName = ({ calendarSlug, calendarName }) => {
  const [name, setName] = useState(calendarName);

  useEffect(() => {
    if (calendarSlug && !calendarName) {
      const fetchCalendar = async () => {
        const response = await getCalendarDetails(calendarSlug);

        if (response?.type === 'success') {
          setName(response?.data.calendar.name);
        }
      };

      fetchCalendar();
    }
  }, [calendarSlug, calendarName]);

  return (
    <Text variant="body1" fontWeight={300} mb="8px">
      {calendarName || name}
    </Text>
  );
};
